import { Destructor } from '@deqo/foundation';
import { Component } from './Component';
import { Convention } from './Convention';
import { ComponentType } from './Interfaces';
import { Manifest } from './Manifest';

export type TriggerInstaller = (trigger: Trigger) => Destructor;

export type TriggerCallback<T = Component> = (hits: T[]) => void;
export type TriggerForEachCallback<T = Component> = (hit: T) => void;

export const SimpleTriggerHook = { all: ['@created', '@deleted', '@appended', '@truncated', '@placed'] as const };
export type SimpleTriggerHook = typeof SimpleTriggerHook.all[number];

export const ParameterizedTriggerHook = { all: ['@changesTo', '@manipulated'] as const };
export type ParameterizedTriggerHook = typeof ParameterizedTriggerHook.all[number];

export type TriggerHook = SimpleTriggerHook | ParameterizedTriggerHook;

export type TriggerHookConfig =
    Partial<Record<SimpleTriggerHook, true>> &
    Partial<Record<ParameterizedTriggerHook, string[] | true>>;

export interface Trigger {
    id: string;
    priority: boolean;
    types: string[];
    hooks: TriggerHookConfig;
    handler: TriggerCallback<Component>;
    includeDead?: boolean;
}

export type TriggerManipulationType = 'translate' | 'scale' | 'rotate' | 'tweak';

export interface TriggerBuilderDefineHooks<X, T> {
    includingDead(): this & TriggerBuilderDefineType<X>;
    onCreated(): this & TriggerBuilderDefineType<X>;
    onDeleted(): this & TriggerBuilderDefineType<X>;
    onPlaced(): this & TriggerBuilderDefineType<X>;
    onManipulated(...types: TriggerManipulationType[]): this & TriggerBuilderDefineType<X>;
    onChangesTo(...properties: (keyof T)[]): this & TriggerBuilderDefineType<X>;
    onChildAdded(): this & TriggerBuilderDefineType<X>;
    onChildRemoved(): this & TriggerBuilderDefineType<X>;
}

export interface TriggerBuilderDefineCallback<X> {
    do(callback: TriggerCallback<X>): Destructor;
    doForEach(callback: TriggerForEachCallback<X>): Destructor;
}

export interface TriggerBuilderInitial {
    for<T extends Component>(type: ComponentType<T>): TriggerBuilderDefineHooks<T, T> & TriggerBuilderDefineCallback<T>;
    for<T>(type: Convention<T>): TriggerBuilderDefineHooks<T, T> & TriggerBuilderDefineCallback<T>;
    forAllTypes(): TriggerBuilderDefineHooks<any, any> & TriggerBuilderDefineCallback<any>;
}

export interface TriggerBuilderDefineType<X> {
    for<T extends Component>(type: ComponentType<T>): TriggerBuilderDefineHooks<X | T, T> & TriggerBuilderDefineCallback<X | T>;
    forAllTypes(): TriggerBuilderDefineHooks<any, any> & TriggerBuilderDefineCallback<any>;
    // for(...typeNames: string[]): TriggerBuilderDefineHooks<any, any> & TriggerBuilderDefineCallback<any>;
}

export interface TriggerOptions {
    priority: boolean;
}

export class TriggerBuilder implements
    TriggerBuilderInitial,
    TriggerBuilderDefineType<any>,
    TriggerBuilderDefineHooks<any, any>,
    TriggerBuilderDefineCallback<any> {

    private config: Partial<Trigger>;
    private install: TriggerInstaller;

    constructor(id: string, priority: boolean, install: TriggerInstaller) {
        this.config = { id, priority, types: [], hooks: {} };
        this.install = install;
    }

    public for(typeOrConvention: any): any {
        const types = (typeOrConvention instanceof Convention)
            ? Manifest.getTypesByConvention(typeOrConvention)
            : [typeOrConvention];

        this.config.types = types.map(x => Component.describe(x).name);
        return this;
    }

    public forAllTypes(): any {
        this.config.types = ['*'];
        return this;
    }

    public includingDead(): any {
        this.config.includeDead = true;
        return this;
    }

    public onCreated(): any {
        this.setHook('@created');
        return this;
    }

    public onDeleted(): any {
        this.setHook('@deleted');
        return this;
    }

    public onPlaced(): any {
        this.setHook('@placed');
        return this;
    }

    public onManipulated(...types: TriggerManipulationType[]): any {
        this.appendHook('@manipulated', types);
        return this;
    }

    public onChangesTo(...args: any[]): any {
        if (this.config.hooks['@changesTo'] !== true) {
            this.appendHook('@changesTo', args);
        }
        return this;
    }

    public onChildAdded(): any {
        this.setHook('@appended');
        return this;
    }

    public onChildRemoved(): any {
        this.setHook('@truncated');
        return this;
    }

    public do(handler: any): Destructor {
        this.config.handler = handler;
        return this.install(this.config as Trigger);
    }

    public doForEach(handler: any): Destructor {
        return this.do(hits => {
            for (const h of hits) {
                handler(h);
            }
        });
    }

    private setHook(hook: TriggerHook) {
        this.config.hooks[hook] = true;
    }

    private appendHook(hook: ParameterizedTriggerHook, params?: string[]) {
        if (this.config.hooks[hook] === true) return;
        this.config.hooks[hook] = [...(this.config.hooks[hook] as any ?? []), ...params];
    }

    // private expandPotentialFields(fieldsOrProps:string[]) {
    //     return fieldsOrProps.flatMap(x => {
    //         Manifest.describeField(this.)
    //     })
    // }
}

